import { captureLoaded } from '../../../lib/funnels/functions/v1/metrics/captureLoaded.js'
import { getSessionValue } from '../../../lib/funnels/functions/v1/default/getSessionValue.js'
import {
  defineCustomElements,
  initializeSession,
  populateStateOptions,
  setupGoogleMaps,
  setupAddressToggle,
  setupOtpUI,
  setupOtpListener,
  createProgressUpdater,
  createFrameTransition,
  setupInputListeners,
  setupContinueButtons,
  setupEnterKeyListeners,
  setupPreviousButtons,
  setupSidebarToggle,
  createFrameSubmitHandler,
  createFormSubmitHandler,
  setupDisclaimerPopup,
  getFrameInfo,
  updateButtonColor,
  SESSION_ID,
} from '../shared.js'

import '../shared.css'
import './index.css'
import { updateSession } from '../../../lib/funnels/functions/v1/default/updateSession.js'

// TODO: Add logic fetch cooldown if user goes back

document.addEventListener('DOMContentLoaded', () => {
  updateSession(SESSION_ID, 'debtOwed', '9999')
  captureLoaded()

  // Define custom elements
  defineCustomElements()

  // Initialize session data
  initializeSession()

  // Get DOM elements
  /** @type {HTMLElement | null} */
  const progressIndicator = document.querySelector('.progress-indicator')
  /** @type {HTMLElement | null} */
  const progressBarFill = document.querySelector('.progress-bar-fill')
  /** @type {HTMLElement | null} */
  const sidebarTarget = document.querySelector('.js-sidebar-target')
  /** @type {HTMLElement | null} */
  const sidebar = document.querySelector('.js-sidebar')
  /** @type {HTMLElement | null} */
  const otp = document.getElementById('otp')
  /** @type {HTMLSelectElement | null} */
  const stateSelect = /** @type {HTMLSelectElement | null} */ (
    document.getElementById('state')
  )
  /** @type {HTMLElement | null} */
  const addressToggle = document.getElementById('toggleAddress')

  // Set up state options
  if (stateSelect) {
    populateStateOptions(stateSelect)
  }

  // Set up Google Maps
  setupGoogleMaps()

  // Set up address toggle
  if (addressToggle) {
    setupAddressToggle(addressToggle)
  }

  /**
   * Auto-fill form fields with current session values
   */
  function prepopFormFields() {
    // Get current session values (these may have been updated after OTP verification)
    const sessionFirstName = getSessionValue(SESSION_ID, 'firstName') || ''
    const sessionLastName = getSessionValue(SESSION_ID, 'lastName') || ''
    const sessionEmail =
      getSessionValue(SESSION_ID, 'email') ||
      getSessionValue(SESSION_ID, 'mainEmail') ||
      ''
    const sessionBirthdate = getSessionValue(SESSION_ID, 'birthdate') || ''
    const sessionMainAddressLine =
      getSessionValue(SESSION_ID, 'mainAddressLine') || ''
    const sessionMainCity = getSessionValue(SESSION_ID, 'mainCity') || ''
    const sessionMainState = getSessionValue(SESSION_ID, 'mainState') || ''
    const sessionMainZipCode = getSessionValue(SESSION_ID, 'mainZipCode') || ''

    // Get form input elements
    const nameInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('name')
    )
    const emailInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('email')
    )
    const birthdateInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('birthdate')
    )
    const addressInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('addressLine')
    )
    const cityInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('city')
    )
    const zipInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('zipcode')
    )
    const stateSelectElement = /** @type {HTMLSelectElement | null} */ (
      document.getElementById('state')
    )

    // For name field, try firstName + lastName first, then fallback to name/fullName
    let nameToUse = ''
    if (sessionFirstName && sessionLastName) {
      nameToUse = `${sessionFirstName} ${sessionLastName}`.trim()
    }

    if (nameInput && nameToUse && !nameInput.value) {
      nameInput.value = nameToUse
    }

    if (emailInput && sessionEmail && !emailInput.value) {
      emailInput.value = sessionEmail
    }

    if (birthdateInput && sessionBirthdate && !birthdateInput.value) {
      // Format birthdate from YYYY-MM-DD to MM/DD/YYYY
      const [year, month, day] = sessionBirthdate.split('-')
      const formattedBirthdate = `${month}/${day}/${year}`
      birthdateInput.value = formattedBirthdate
    }

    // Populate address field
    if (addressInput && sessionMainAddressLine && !addressInput.value) {
      addressInput.value = sessionMainAddressLine
    }

    // Check if we need to expand address fields for city, state, zip autofill
    const shouldExpandAddressFields =
      (sessionMainCity && cityInput && !cityInput.value) ||
      (sessionMainState && stateSelectElement && !stateSelectElement.value) ||
      (sessionMainZipCode && zipInput && !zipInput.value)

    // Expand address fields if any city, state, or zip data is available but field is empty
    if (shouldExpandAddressFields) {
      const addressFields = document.getElementById('addressFields')
      const addressToggleElement = document.getElementById('toggleAddress')

      if (
        addressFields &&
        addressToggleElement &&
        addressFields.classList.contains('hidden')
      ) {
        addressFields.classList.remove('hidden')
        addressToggleElement.classList.add('open')
        addressToggleElement.textContent = 'Back to address search'
      }
    }

    // Populate city field
    if (cityInput && sessionMainCity && !cityInput.value) {
      // Handle case where city field contains street address + city (e.g., "123 test way, testCity")
      let cityToUse = sessionMainCity
      if (sessionMainCity.includes(',')) {
        // Split by comma and take the second part (city name)
        const parts = sessionMainCity.split(',').map((part) => part.trim())
        cityToUse = parts[1] // Get the city name after the first comma
      }
      cityInput.value = cityToUse
    }

    // Populate zip code field
    if (zipInput && sessionMainZipCode && !zipInput.value) {
      zipInput.value = sessionMainZipCode
    }

    // Populate state select
    if (stateSelectElement && sessionMainState && !stateSelectElement.value) {
      stateSelectElement.value = sessionMainState
    }

    // Update button color after populating fields
    const currentFrame = document.querySelector(
      '.onboarding-form-frame:not(.hidden)',
    )
    if (currentFrame) {
      const firstInput = /** @type {HTMLInputElement | null} */ (
        // checks all inputs so just use the first and run function
        currentFrame.querySelector('.onboarding-form-input')
      )
      if (firstInput) {
        updateButtonColor(firstInput)
      }
    }
  }

  // Constants
  const frameInfo = getFrameInfo() // Get all frame info from HTML
  const totalFrames = frameInfo.questionFrames - 1
  const loadingFrameNumber = frameInfo.loadingFrameNumber

  // Create progress updater
  const updateProgress = createProgressUpdater(
    progressIndicator,
    progressBarFill,
    totalFrames,
    loadingFrameNumber,
    2, // OTP frame number
  )

  // Create form submit handler
  const handleOnboardingSubmit = createFormSubmitHandler()

  // Create frame transition function with loading frame callback
  const transitionToFrame = createFrameTransition(
    updateProgress,
    (/** @type {number} */ frame) => {
      // If transitioning to loading frame, trigger form submission
      if (frame === loadingFrameNumber) {
        handleOnboardingSubmit()
      }
      prepopFormFields()
    },
  )

  // Create frame submission handler
  const handleFrameSubmit = createFrameSubmitHandler(
    transitionToFrame,
    setupOtpUI,
    1, // Phone frame number
    true, // useVeracity flag
  )

  // Set up OTP listener
  if (otp) {
    setupOtpListener(otp, transitionToFrame, true) // useVeracity flag
  }

  // Set up input listeners
  setupInputListeners()

  // Set up continue buttons
  setupContinueButtons(handleFrameSubmit)

  // Set up enter key listeners
  setupEnterKeyListeners(handleFrameSubmit)

  // Set up previous buttons
  setupPreviousButtons(transitionToFrame)

  // Set up sidebar toggle
  setupSidebarToggle(sidebar, sidebarTarget)

  // Set up disclaimer popup
  setupDisclaimerPopup()

  // Initialize progress
  updateProgress(1)
})
